'use client';

import { LoadingImage } from '@modules/common/components/loading-image';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useState } from 'react';
import type { ImageGalleryProps } from '.';
import { DotsNavBar } from './dots-nav-bar';
import { NavButtons } from './nav-buttons';

export const MobileImageGallery = ({
  productTitle,
  images,
}: ImageGalleryProps): React.JSX.Element => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    dragFree: false,
    slidesToScroll: 1,
  });

  useEffect(() => {
    if (!emblaApi) {
      return;
    }
    const onSelect = () => setCurrentIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  return (
    <section
      aria-label={`${productTitle} images`}
      aria-roledescription="carousel"
      className="desktop:hidden"
    >
      <div className="relative w-full">
        <div className="w-full overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {images.map((image, index) => (
              // biome-ignore lint/a11y/useSemanticElements: role="group" is the correct ARIA pattern for carousel slides
              <div
                aria-label={`${index + 1} of ${images.length}`}
                aria-roledescription="slide"
                className="relative w-full shrink-0 aspect-square bg-[#F3F4F3] sm:aspect-auto sm:h-[543px]"
                key={image.url ?? index}
                role="group"
              >
                <LoadingImage
                  alt={`${productTitle} - photo ${index + 1} of ${images.length}`}
                  className="object-contain mix-blend-multiply"
                  fill
                  hideSkeleton={index !== 0}
                  fetchPriority={index === 0 ? 'high' : undefined}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  quality={index === 0 ? 70 : 60}
                  sizes="(max-width: 768px) 100vw, 343px"
                  src={image.url}
                />
              </div>
            ))}
          </div>
        </div>
        <NavButtons
          count={images.length}
          currentIndex={currentIndex}
          onPrev={() => emblaApi?.scrollPrev()}
          onNext={() => emblaApi?.scrollNext()}
        />
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
          <DotsNavBar count={images.length} currentIndex={currentIndex} />
        </div>
      </div>
    </section>
  );
};
